import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import "./InvoicePaymentPage.css";


const PAYMENT_ACCESS_MIN_LENGTH = 16;
const PAYMENT_ACCESS_MAX_LENGTH = 512;


export function parseInvoicePaymentPath(
  pathname,
  hash
) {
  const rawPath = String(
    pathname || ""
  ).trim();

  if (
    rawPath !== "/pay"
    && rawPath !== "/pay/"
  ) {
    return null;
  }

  const rawHash = String(
    hash || ""
  ).trim();

  if (!rawHash.startsWith("#")) {
    return null;
  }

  let token = "";

  try {
    token = decodeURIComponent(
      rawHash.slice(1)
    ).trim();
  } catch {
    return null;
  }

  if (
    token.length < PAYMENT_ACCESS_MIN_LENGTH
    || token.length > PAYMENT_ACCESS_MAX_LENGTH
    || token.includes("/")
    || token.includes("#")
  ) {
    return null;
  }

  return {
    token,
  };
}


function formatMoneyMinor(
  amountMinor,
  currency
) {
  const amount = Number(
    amountMinor
  );

  const normalizedCurrency = String(
    currency || ""
  )
    .trim()
    .toUpperCase();

  if (
    !Number.isFinite(amount)
    || !normalizedCurrency
  ) {
    return "—";
  }

  try {
    return new Intl.NumberFormat(
      "en-US",
      {
        style: "currency",
        currency: normalizedCurrency,
      }
    ).format(
      amount / 100
    );
  } catch {
    return (
      `${normalizedCurrency} `
      + (amount / 100).toFixed(2)
    );
  }
}


function formatDate(value) {
  const normalized = String(
    value || ""
  ).trim();

  if (!normalized) {
    return "—";
  }

  const parts = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})$/
  );

  if (!parts) {
    return normalized;
  }

  const date = new Date(
    Number(parts[1]),
    Number(parts[2]) - 1,
    Number(parts[3])
  );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return normalized;
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}


async function readJsonResponse(
  response
) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}


function browserRedirect(
  url
) {
  window.location.assign(url);
}


function OasysMark() {
  return (
    <div
      className="invoice-payment-mark"
      aria-hidden="true"
    >
      O
    </div>
  );
}


function LoadingState() {
  return (
    <div
      className="invoice-payment-page"
      data-testid="invoice-payment-loading"
    >
      <main
        className="invoice-payment-card"
        aria-busy="true"
      >
        <div className="invoice-payment-brand">
          <OasysMark />
          <div>
            <p className="invoice-payment-eyebrow">
              OASYS Solutions
            </p>
            <span>Secure invoice payment</span>
          </div>
        </div>

        <div className="invoice-payment-loading-line" />
        <div className="invoice-payment-loading-line is-short" />
        <div className="invoice-payment-loading-block" />

        <p className="invoice-payment-muted">
          Loading invoice details…
        </p>
      </main>
    </div>
  );
}


function UnavailableState({
  retry,
  retryable = false,
}) {
  return (
    <div className="invoice-payment-page">
      <main
        className="invoice-payment-card"
        aria-labelledby="invoice-payment-title"
      >
        <div className="invoice-payment-brand">
          <OasysMark />
          <div>
            <p className="invoice-payment-eyebrow">
              OASYS Solutions
            </p>
            <span>Secure invoice payment</span>
          </div>
        </div>

        <div
          className="invoice-payment-state is-unavailable"
          aria-hidden="true"
        >
          !
        </div>

        <p className="invoice-payment-kicker">
          Payment link unavailable
        </p>

        <h1 id="invoice-payment-title">
          This invoice payment link is not available.
        </h1>

        <p>
          The link may be invalid, revoked, or no longer
          available for payment.
        </p>

        <div className="invoice-payment-actions">
          {retryable ? (
            <button
              type="button"
              className="invoice-payment-secondary"
              onClick={retry}
            >
              Try again
            </button>
          ) : null}

          <a
            className="invoice-payment-secondary"
            href="mailto:billing@oasyshq.com"
          >
            Contact billing
          </a>
        </div>

        <footer>
          OASYS does not request payment credentials on this
          page. Payment details are entered only with the
          authorized payment provider.
        </footer>
      </main>
    </div>
  );
}


export default function InvoicePaymentPage({
  token,
  v1BaseUrl,
  redirectToCheckout = browserRedirect,
}) {
  const [loadState, setLoadState] = useState(
    "loading"
  );

  const [invoice, setInvoice] = useState(
    null
  );

  const [submitting, setSubmitting] = useState(
    false
  );

  const [paymentNotice, setPaymentNotice] = useState(
    ""
  );

  const normalizedBase = useMemo(
    () => String(
      v1BaseUrl || ""
    ).replace(/\/+$/, ""),
    [v1BaseUrl]
  );

  const invoiceUrl = useMemo(
    () => (
      `${normalizedBase}/payments/invoice-access`
    ),
    [
      normalizedBase,
    ]
  );

  const authorizationHeader = useMemo(
    () => (
      `Bearer ${String(token || "")}`
    ),
    [token]
  );

  const loadInvoice = useCallback(
    async ({
      signal,
    } = {}) => {
      setLoadState(
        "loading"
      );
      setPaymentNotice(
        ""
      );

      try {
        const response = await fetch(
          invoiceUrl,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: authorizationHeader,
            },
            credentials: "omit",
            cache: "no-store",
            referrerPolicy: "no-referrer",
            signal,
          }
        );

        const payload = await readJsonResponse(
          response
        );

        if (!response.ok) {
          setInvoice(
            null
          );

          setLoadState(
            response.status === 404
              ? "unavailable"
              : "error"
          );

          return;
        }

        const nextInvoice = (
          payload
          && typeof payload === "object"
          && payload.invoice
          && typeof payload.invoice === "object"
        )
          ? payload.invoice
          : null;

        if (!nextInvoice) {
          setInvoice(
            null
          );
          setLoadState(
            "error"
          );
          return;
        }

        setInvoice(
          nextInvoice
        );
        setLoadState(
          "ready"
        );
      } catch (error) {
        if (
          error?.name === "AbortError"
        ) {
          return;
        }

        setInvoice(
          null
        );
        setLoadState(
          "error"
        );
      }
    },
    [
      invoiceUrl,
      authorizationHeader,
    ]
  );

  useEffect(
    () => {
      const controller = new AbortController();

      loadInvoice({
        signal: controller.signal,
      });

      return () => {
        controller.abort();
      };
    },
    [loadInvoice]
  );

  const startCheckout = useCallback(
    async () => {
      if (
        submitting
        || !invoice
        || !invoice.can_pay
      ) {
        return;
      }

      setSubmitting(
        true
      );
      setPaymentNotice(
        ""
      );

      try {
        const response = await fetch(
          `${invoiceUrl}/checkout`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: authorizationHeader,
            },
            credentials: "omit",
            cache: "no-store",
            referrerPolicy: "no-referrer",
          }
        );

        const payload = await readJsonResponse(
          response
        );

        if (!response.ok) {
          if (
            response.status === 404
          ) {
            setInvoice(
              null
            );
            setLoadState(
              "unavailable"
            );
            return;
          }

          setPaymentNotice(
            "Checkout is temporarily unavailable. Please try again."
          );
          return;
        }

        const payment = (
          payload
          && typeof payload === "object"
          && payload.payment
          && typeof payload.payment === "object"
        )
          ? payload.payment
          : {};

        const status = String(
          payment.status || ""
        )
          .trim()
          .toLowerCase();

        if (
          status === "open"
          && payment.can_redirect === true
          && payment.checkout_url
        ) {
          let checkoutUrl;

          try {
            checkoutUrl = new URL(
              String(
                payment.checkout_url
              )
            );
          } catch {
            setPaymentNotice(
              "Checkout is temporarily unavailable. Please try again."
            );
            return;
          }

          if (
            checkoutUrl.protocol !== "https:"
          ) {
            setPaymentNotice(
              "Checkout is temporarily unavailable. Please try again."
            );
            return;
          }

          redirectToCheckout(
            checkoutUrl.toString()
          );

          return;
        }

        if (
          status === "paid"
        ) {
          setInvoice(
            (current) => ({
              ...(current || {}),
              status: "paid",
              can_pay: false,
              paid: true,
              void: false,
            })
          );

          setPaymentNotice(
            "This invoice is already paid."
          );

          return;
        }

        if (
          status === "processing"
        ) {
          setInvoice(
            (current) => ({
              ...(current || {}),
              status: "processing",
              can_pay: false,
            })
          );

          setPaymentNotice(
            "Payment is processing. No additional payment attempt was created."
          );

          return;
        }

        setPaymentNotice(
          "Checkout is not currently available. Please try again."
        );
      } catch {
        setPaymentNotice(
          "Checkout is temporarily unavailable. Please try again."
        );
      } finally {
        setSubmitting(
          false
        );
      }
    },
    [
      authorizationHeader,
      invoice,
      invoiceUrl,
      redirectToCheckout,
      submitting,
    ]
  );

  if (
    loadState === "loading"
  ) {
    return <LoadingState />;
  }

  if (
    loadState === "unavailable"
  ) {
    return (
      <UnavailableState
        retry={loadInvoice}
        retryable={false}
      />
    );
  }

  if (
    loadState === "error"
  ) {
    return (
      <UnavailableState
        retry={loadInvoice}
        retryable
      />
    );
  }

  const paid = Boolean(
    invoice?.paid
    || String(
      invoice?.status || ""
    ).toLowerCase() === "paid"
  );

  const voided = Boolean(
    invoice?.void
    || String(
      invoice?.status || ""
    ).toLowerCase() === "void"
  );

  const canPay = Boolean(
    invoice?.can_pay
  ) && !paid && !voided;

  const amount = formatMoneyMinor(
    invoice?.amount_minor,
    invoice?.currency
  );

  return (
    <div className="invoice-payment-page">
      <main
        className="invoice-payment-card"
        aria-labelledby="invoice-payment-title"
      >
        <div className="invoice-payment-brand">
          <OasysMark />
          <div>
            <p className="invoice-payment-eyebrow">
              OASYS Solutions
            </p>
            <span>Secure invoice payment</span>
          </div>
        </div>

        <section className="invoice-payment-heading">
          <div>
            <p className="invoice-payment-kicker">
              Invoice
            </p>

            <h1 id="invoice-payment-title">
              {invoice.invoice_number}
            </h1>
          </div>

          <span
            className={`invoice-payment-status is-${String(
              invoice.status || "unknown"
            ).toLowerCase()}`}
          >
            {paid
              ? "Paid"
              : voided
                ? "Void"
                : String(
                    invoice.status || "Invoice"
                  )}
          </span>
        </section>

        <section
          className="invoice-payment-amount"
          aria-label="Invoice amount"
        >
          <span>Amount due</span>
          <strong>{amount}</strong>
          <small>
            {String(
              invoice.currency || ""
            ).toUpperCase()}
          </small>
        </section>

        <dl className="invoice-payment-details">
          <div>
            <dt>Invoice date</dt>
            <dd>
              {formatDate(
                invoice.issue_date
              )}
            </dd>
          </div>

          <div>
            <dt>Due date</dt>
            <dd>
              {formatDate(
                invoice.due_date
              )}
            </dd>
          </div>
        </dl>

        {paid ? (
          <section
            className="invoice-payment-message is-success"
            role="status"
          >
            <strong>Payment complete</strong>
            <span>
              This invoice is recorded as paid in OASYS.
            </span>
          </section>
        ) : null}

        {voided ? (
          <section
            className="invoice-payment-message"
            role="status"
          >
            <strong>Invoice unavailable</strong>
            <span>
              This invoice is void and cannot be paid.
            </span>
          </section>
        ) : null}

        {!paid && !voided && !canPay ? (
          <section
            className="invoice-payment-message"
            role="status"
          >
            <strong>Payment is not currently available</strong>
            <span>
              No payment will be started from this page.
            </span>
          </section>
        ) : null}

        {paymentNotice ? (
          <div
            className="invoice-payment-notice"
            role="status"
          >
            {paymentNotice}
          </div>
        ) : null}

        {canPay ? (
          <div className="invoice-payment-actions">
            <button
              type="button"
              className="invoice-payment-primary"
              onClick={startCheckout}
              disabled={submitting}
            >
              {submitting
                ? "Opening secure checkout…"
                : `Pay ${amount}`}
            </button>
          </div>
        ) : null}

        <div className="invoice-payment-provider-note">
          <strong>Secure payment</strong>
          <span>
            Selecting Pay Invoice opens the authorized payment
            provider. OASYS does not collect card or bank
            credentials on this page.
          </span>
        </div>

        <footer>
          Need help?{" "}
          <a href="mailto:billing@oasyshq.com">
            billing@oasyshq.com
          </a>
        </footer>
      </main>
    </div>
  );
}
